import { t } from "@lingui/macro";
import { Separator } from "@reactive-resume/ui";

import { Logo } from "@/client/components/logo";
import { ThemeSwitch } from "@/client/components/theme-switch";

const PRODUCT_LINKS = [
  { label: "Features", href: "/#ai-writer" },
  { label: "Templates", href: "/#templates" },
  { label: "Pricing", href: "/#pricing" },
  { label: "AI Tools", href: "/#ai-writer" },
];

const RESOURCE_LINKS = [
  { label: "Blog", href: "/blog/" },
  { label: "Resume Tips", href: "/blog/category/resume-writing/" },
  { label: "Interview Guide", href: "/blog/category/interview-preparation/" },
  { label: "Career Trends", href: "/blog/category/career-trends/" },
  { label: "Contact Us", href: "/contact" },
];

const COMPARE_LINKS = [
  { label: "vs Zety", href: "/blog/ai-career-tools/careerbldr-vs-zety/" },
  { label: "vs Resume.io", href: "/blog/ai-career-tools/careerbldr-vs-resume-io/" },
  { label: "vs Kickresume", href: "/blog/ai-career-tools/careerbldr-vs-kickresume/" },
  { label: "vs Canva", href: "/blog/ai-career-tools/careerbldr-vs-canva-resume/" },
  { label: "vs Resume Genius", href: "/blog/ai-career-tools/careerbldr-vs-resume-genius/" },
];

const LEGAL_LINKS = [
  { label: "Terms of Service", href: "/terms" },
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Cookie Policy", href: "/cookies" },
];

const FooterColumn = ({
  title,
  links,
}: {
  title: string;
  links: { label: string; href: string }[];
}) => (
  <div>
    <h3 className="mb-3 text-sm font-semibold">{title}</h3>
    <ul className="space-y-2.5">
      {links.map((link) => (
        <li key={link.href}>
          <a
            href={link.href}
            className="text-sm text-foreground/50 transition-colors hover:text-foreground/80"
          >
            {link.label}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

export const Footer = () => (
  <footer className="bg-background">
    <Separator />

    <div className="container py-12">
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <div className="mb-4 flex items-center gap-2.5">
            <Logo size={36} className="-ml-1" />
            <span className="text-lg font-semibold tracking-tight">{t`CareerBldr`}</span>
          </div>
          <p className="max-w-xs text-sm leading-relaxed text-foreground/50">
            {t`AI resume builder with ATS scoring, job matching, and cover letters. Plans start at $9/month with a 3-day free trial.`}
          </p>
          <p className="mt-3 max-w-xs text-xs leading-relaxed text-foreground/30">
            {t`Built by Studio Algorithm, a mission-driven organization.`}
          </p>
        </div>

        <FooterColumn title={t`Product`} links={PRODUCT_LINKS} />
        <FooterColumn title={t`Resources`} links={RESOURCE_LINKS} />
        <FooterColumn title={t`Compare`} links={COMPARE_LINKS} />
        <div className="space-y-6">
          <FooterColumn title={t`Legal`} links={LEGAL_LINKS} />
        </div>
      </div>

      <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border/30 pt-8 sm:flex-row">
        <p className="text-xs text-foreground/40">
          &copy; {new Date().getFullYear()} CareerBldr.com &middot; {t`Built by`}{" "}
          <a
            target="_blank"
            rel="noopener noreferrer nofollow"
            href="https://studioalgorithms.com"
            className="hover:text-foreground/60"
          >
            Studio Algorithm
          </a>
        </p>
        <div className="flex items-center gap-4">
          <a
            href="https://x.com/careerbldr_"
            target="_blank"
            rel="noopener noreferrer me"
            aria-label={t`Follow CareerBldr on X`}
            title={t`Follow CareerBldr on X`}
            className="text-foreground/40 transition-colors hover:text-foreground/80"
          >
            <svg
              role="img"
              aria-hidden="true"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="size-4"
            >
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
            </svg>
          </a>
          <ThemeSwitch />
        </div>
      </div>
    </div>
  </footer>
);
